import { useCallback, useMemo } from 'react';

import { Box, Skeleton, Stack } from '@mui/material';

import {
  type SortableItemRenderProps,
  SortableList,
} from '@modules/_shared/components/sortable-list/SortableList';
import { SortableTableHeader } from '@modules/_shared/components/sortable-list/SortableTableHeader';
import {
  SortableTableRow,
  TableRow,
} from '@modules/_shared/components/sortable-list/SortableTableRow';
import { SortableTableUploadingRow } from '@modules/_shared/components/sortable-list/SortableTableUploadingRow';
import {
  type SortableTableColDef,
  type SortableTableProps,
  type UploadingItem,
} from '@modules/_shared/components/sortable-list/types';

export const SortableTable = <T extends { id: string }>({
  items,
  columns,
  onReorder,
  enableDragAndDrop = true,
  uploadingItems = [],
  renderUploadingItem,
  showHeader = true,
  loading = false,
  skeletonRows = 5,
  onRowClick,
  selectedItemId,
  sx,
  rowGap = 0,
  rowHover = true,
  dragHandleWidth = 32,
}: SortableTableProps<T>) => {
  const visibleColumns = useMemo(
    () => columns.filter(col => !col.hide),
    [columns],
  );

  const resolvedUploadingColumns = useMemo<
    SortableTableColDef<UploadingItem>[]
  >(
    () =>
      visibleColumns.map((col, index) => ({
        field: col.field,
        width: col.width,
        flex: col.flex,
        minWidth: col.minWidth,
        align: col.align,
        renderCell:
          index === 0 && renderUploadingItem
            ? ({ row }) => renderUploadingItem(row)
            : () => null,
      })),
    [visibleColumns, renderUploadingItem],
  );

  const isDndEnabled = enableDragAndDrop && onReorder != null;

  const renderSortableRow = useCallback(
    ({ item, isDragOverlay }: SortableItemRenderProps<T>) => (
      <SortableTableRow
        key={item.id}
        item={item}
        columns={columns}
        isDragOverlay={isDragOverlay}
        isSelected={selectedItemId === item.id}
        onClick={onRowClick ? () => onRowClick(item) : undefined}
        rowHover={isDragOverlay ? false : rowHover}
        dragHandleWidth={dragHandleWidth}
      />
    ),
    [columns, onRowClick, selectedItemId, rowHover, dragHandleWidth],
  );

  const header = showHeader ? (
    <SortableTableHeader
      columns={columns}
      showDragHandleSpacer={isDndEnabled}
      dragHandleWidth={dragHandleWidth}
    />
  ) : null;

  const uploadingContent =
    uploadingItems.length > 0
      ? uploadingItems.map(item => (
          <SortableTableUploadingRow
            key={item.id}
            item={item}
            columns={resolvedUploadingColumns}
            dragHandleWidth={dragHandleWidth}
          />
        ))
      : null;

  if (loading) {
    return (
      <Box sx={sx}>
        {header}
        <Stack
          sx={{
            gap: rowGap,
          }}
        >
          {Array.from({ length: skeletonRows }).map((_, index) => (
            <Box
              key={`skeleton-${index}`}
              sx={theme => ({
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: '12px',
                py: 1,
                backgroundColor: (theme.vars ?? theme).palette.background.paper,
                borderBottom: `1px solid ${
                  (theme.vars ?? theme).palette.divider
                }`,
              })}
            >
              {isDndEnabled && (
                <Box sx={{ width: dragHandleWidth, flexShrink: 0 }}>
                  <Skeleton variant="rectangular" width={24} height={24} />
                </Box>
              )}
              {visibleColumns.map(column => (
                <Box
                  key={column.field}
                  sx={{
                    width: column.width,
                    flex: column.flex,
                    minWidth: column.minWidth ?? 0,
                    flexShrink: column.width ? 0 : undefined,
                  }}
                >
                  <Skeleton variant="text" width="80%" />
                </Box>
              ))}
            </Box>
          ))}
        </Stack>
      </Box>
    );
  }

  if (!isDndEnabled) {
    return (
      <Box sx={sx}>
        {header}
        <Stack
          sx={{
            gap: rowGap,
          }}
        >
          {items.map(item => (
            <TableRow
              key={item.id}
              item={item}
              columns={columns}
              isSelected={selectedItemId === item.id}
              onClick={onRowClick ? () => onRowClick(item) : undefined}
              rowHover={rowHover}
            />
          ))}
          {uploadingContent}
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={sx}>
      {header}
      <SortableList
        items={items}
        onReorder={onReorder}
        renderItem={renderSortableRow}
        gap={rowGap}
      />
      {uploadingContent}
    </Box>
  );
};
